// Rental Unit Type selector (dropdown only)
import { useEffect, useState, useCallback } from "react";
import { useGlobal, setGlobal } from "reactn";
import useLanguage from "../useLanguage";
import Dropdown from "./Dropdown";
import { getProp, inArray, isArray, listSize, stringIncludes } from "../../lib/util";
import { isCondo } from "../../lib/features-config";
import { setStoredValue, setAndStoreGlobalValue } from "../../lib/stored-data";
import { RENTAL, SALES } from "../../lib/globals";
import getLogger from "../../lib/debug-logger";

import "../../styles/avm/dropdown.scss";

const log = getLogger("RentalTypeDropdown", 2);

// reset globals related to rental valuation if rental type is empty
const dropRentalVars = () => {
  setGlobal({
    RENTAL_VALUE_ERROR: "",
    RENTAL_RANGE: null,
    RENTAL_VALUE: 0,
  });
};

const RentalTypeDropdown = ({
  defaultValue = null,
  autoFill = false,
  disabled = false,
  showForComparables = false,
}) => {
  const [dataSchema] = useGlobal("DATA_SCHEMA");
  const [showRental] = useGlobal("SHOW_RENTAL"); // this is set by Province, not by avaiable product
  const [hasRental] = useGlobal("PROD_AVAIL_RENTAL_VAL");
  const [readonly] = useGlobal("READONLY_PREFILL");
  const [propertyType] = useGlobal("PROPERTY_TYPE");
  const [rentalType, setRentalType] = useGlobal("RENTAL_TYPE");

  // default Rental Type selection may be set by Architectural Style feature
  const [archStyleRes] = useGlobal("ArchitecturalStyleType");
  const [archStyleCondo] = useGlobal("MultiResidentialStyleType");

  const [archStyle, setArchStyle] = useState(""); // actual architectural style

  const [typeValues, setTypeValues] = useState(null);

  const { t } = useLanguage();

  const setAndStoreRentalType = useCallback(
    (rentalType) => {
      setRentalType(rentalType);
      setStoredValue("RENTAL_TYPE", rentalType);
      // pre-set Comparables mode based on rental type
      if (showForComparables) {
        setAndStoreGlobalValue("COMPARABLES_MODE", rentalType ? RENTAL : SALES);
      }
    },
    [setRentalType, showForComparables]
  );

  // --------------- defining actual arch style
  useEffect(() => {
    const stl = isCondo(propertyType) ? archStyleCondo : archStyleRes;
    log(`setting Arch Style to '${stl}'`); //------------------------------ log
    setArchStyle(stl);
  }, [propertyType, archStyleCondo, archStyleRes, setArchStyle]);

  // ----------------- providing values for dropdown menu
  useEffect(() => {
    if (!dataSchema) return;

    let vals = getProp(dataSchema, "rentalTypeValues"); // list of values from OSS schema

    if (!isArray(vals)) {
      log(`rentalTypeValues must be an array`); //-------------- log
      return;
    }
    log(`provided Rental Type values`, vals); //----------------- log

    // populating value/label pairs for dropdown;
    // put complete property value in the 1st place, remove Condo and Residential
    vals = vals.filter((it) => !["Complete Property", "Condo", "Residential"].includes(it));
    vals = ["", "Complete Property", ...vals];

    setTypeValues(vals);
    log(`setting Rental Type values`, vals); //----------------- log
  }, [propertyType, dataSchema, setTypeValues]);

  // ------------ Autofill: try to set rental type based on architectural style
  useEffect(() => {
    if (rentalType || !archStyle || !autoFill) return;

    if (!listSize(typeValues)) {
      log(`rental type options not ready`, typeValues); //------------ log
      return; // selector options not ready yet
    }
    log(`selecting Rental Type based on Architectural Style '${archStyle}'`); //---------- log

    // if architectural style matches any option, set that option as default rental type
    let rental = "";
    typeValues.forEach((it) => {
      if (stringIncludes(it, archStyle, true)) {
        rental = it;
        return;
      }
    });
    if (rental && rentalType !== rental) {
      dropRentalVars();
      setAndStoreRentalType(rental);
      log(`setting  Rental Type '${rental}'`); //-------------- log
    }
  }, [typeValues, propertyType, archStyle, autoFill]);

  // setting default value, if provided
  useEffect(() => {
    if (rentalType || !defaultValue || !inArray(typeValues, defaultValue)) return;
    dropRentalVars();
    setAndStoreRentalType(defaultValue);
    log(`setting default Rental Type '${defaultValue}'`); //---------------- log
  }, [defaultValue, typeValues]);

  // Rental Type selection handler
  const handleChange = (val) => {
    dropRentalVars();
    setAndStoreRentalType(val);
    log(`setting Rental Type '${val}'`); //--------------------- log
  };

  //-------------------------------------------
  if ((!showForComparables && (readonly || !hasRental || !showRental)) || !typeValues) return null;
  //-------------------------------------------

  return (
    <Dropdown
      className="feature-value"
      disabled={disabled}
      vals={typeValues}
      labels={typeValues.map((it) => t(it))}
      initValue={rentalType || defaultValue || ""}
      placeholder={t("Not entered")}
      changeCallback={handleChange}
    />
  );
};

export default RentalTypeDropdown;
